import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { elapsed } from "@/lib/format";

async function enablePush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    const reg = await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/vapid-public-key");
    const key = Uint8Array.from(atob(data.public_key.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch { /* non-blocking */ }
}

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const open = async () => {
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data);
    } catch { setItems([]); }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    open();
  };

  return (
    <Popover onOpenChange={(o) => { if (o) { open(); enablePush(); } }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-slate-100">
          <Bell className="h-5 w-5 text-slate-600" />
          {count > 0 && <span data-testid="notification-badge" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">{count}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="font-display font-semibold">Notificaciones</p>
          <Button variant="ghost" size="sm" onClick={markAll} className="h-7 text-xs">Marcar leídas</Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400">Sin notificaciones</p>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => n.deep_link && nav(n.deep_link)}
              className={`block w-full border-b px-4 py-3 text-left hover:bg-slate-50 ${!n.read_at ? "bg-blue-50/50" : ""}`}>
              <p className="text-sm font-medium text-slate-800">{n.title}</p>
              <p className="text-xs text-slate-500">{n.body}</p>
              <p className="mt-0.5 text-[11px] text-slate-400">{elapsed(n.sent_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
